import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import ObjectIcon from '../shared/ObjectIcon';
import { Combobox, comboboxFilterAndLimit, IconSettings } from '@salesforce/design-system-react';
import { getTimekeeperOptions } from '../../../api/emailsApi/time';
import './Timekeepers.scss';
import { cacheTimekeeperSelected } from '../../actions/time';

const timekeeperLookupItem = ({ icon, label }) => {
  return (
    <div className='timekeeper-lookup-menu-option'>
      {icon || ''}
      <span className="slds-listbox__option-text slds-listbox__option-text_entity timekeeper-lookup-menu-option-label">
        {label || ''}
      </span>
    </div>
  );
};
timekeeperLookupItem.displayName = 'CustomMenuItem';


export const Timekeepers = ({ label, fieldName, handleChange, defaultValue }) => {
  // Use current logged in salesforce user as default
  const dispatch = useDispatch();
  const salesforceUserId = useSelector((state) => state.auth.user?.salesforceUserId);
  const salesforceDisplayName = useSelector((state) => state.auth.user?.salesforceDisplayName);
  const cachedSelectedTimekeepers = useSelector((state) => state.time?.timekeepersSelected);

  const [timekeeperOptions, setTimekeeperOptions] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedTimekeeper, setSelectedTimekeeper] = useState({
    selection: [
      {
        label: cachedSelectedTimekeepers[defaultValue] || salesforceDisplayName,
        value: cachedSelectedTimekeepers[defaultValue] ? defaultValue : salesforceUserId,
        icon: <ObjectIcon objectApiName='User' className='timekeeper-lookup-option'/>,
      },
    ],
  });

  const updateTimekeeperOptions = async () => {
    setIsLoading(true);

    let timekeeperOptionsResponse;
    try {
      timekeeperOptionsResponse = await getTimekeeperOptions();
    } catch (e) {
      console.log(e);
      setTimekeeperOptions([{
        label: salesforceDisplayName,
        value: salesforceUserId,
        icon: <ObjectIcon objectApiName='User' className='timekeeper-lookup-option'/>,
      }]);
      setIsLoading(false);
      return;
    }

    const options = timekeeperOptionsResponse.data?.map((timekeeper) => ({
      label: timekeeper.name,
      value: timekeeper.userId,
      icon: <ObjectIcon objectApiName='User' className='timekeeper-lookup-option'/>,
    }));
    setIsLoading(false);
    setTimekeeperOptions(options);
  };

  useEffect(() => {
    // If time record is being edited, display previously selected user. Otherwise default to logged in user
    handleChange(null, { fieldName, value: defaultValue || salesforceUserId });
    updateTimekeeperOptions();
  }, []);

  return (
    <IconSettings iconPath="/assets">
      <Combobox
        classNameContainer={''}
        id="combobox-inline-single"
        required={true}
        name={fieldName}
        errorText={''}
        defaultValue={selectedTimekeeper.selection}
        hasMenuSpinner={isLoading}
        events={{
          onChange: (event, { value }) => {
            setSearchQuery(value);
          },
          onRequestRemoveSelectedOption: (event, data) => {
            setSelectedTimekeeper({
              selection: data.selection,
            });
            handleChange(event, { fieldName, value: null });
          },
          onSelect: (event, data) => {
            setSelectedTimekeeper({
              selection: data.selection,
            });
            handleChange(event, { fieldName, value: data.selection[0].value });
            dispatch(cacheTimekeeperSelected(data.selection[0].value, data.selection[0].label));
          },
        }}
        labels={{
          label,
          placeholder: `Search ${label}`,
          noOptionsFound: isLoading ? '' : 'No timekeepers found',
        }}
        onRenderMenuItem={(timekeeperOptions) => timekeeperLookupItem(timekeeperOptions.option)}
        options={comboboxFilterAndLimit({
          inputValue: searchQuery,
          options: timekeeperOptions,
          selection: selectedTimekeeper.selection,
          limit: 10,
        })}
        menuItemVisibleLength={5}
        selection={selectedTimekeeper.selection}
        value={
          selectedTimekeeper.selection.label
        }
        variant='inline-listbox'
      />
    </IconSettings>
  );
};

Timekeepers.propTypes = {
  label: PropTypes.string,
  fieldName: PropTypes.string,
  handleChange: PropTypes.func,
  defaultValue: PropTypes.string,
};
